import { useEffect, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loader2, CheckCircle2, HelpCircle, Check, ChevronDown } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import Wordmark from '@/components/layout/Wordmark'
import { useBrand, splitBrandName } from '@/hooks/useBrand'

interface ContractItem {
  name: string
  category: string
  billing: string
  unitPriceCents: number
  quantity: number
  term: string
  managementFeePct: number | null
  description: string | null
  deliverables: string[]
}
interface Contract {
  token: string
  status: string
  business: string | null
  contactName: string | null
  contactEmail: string | null
  address: string | null
  monthlyTotalCents: number
  oneTimeTotalCents: number
  signedName: string | null
  items: ContractItem[]
  termsMarkdown: string
}

const TERM_LABEL: Record<string, string> = {
  monthly: 'Monthly',
  '3_month': '3-month term',
  '6_month': '6-month term',
  '12_month': '12-month term',
  one_time: 'One-time',
}

function fmt(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
}

function inlineMd(text: string): ReactNode[] {
  return text
    .split('**')
    .map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>))
}

/** Minimal markdown renderer for the assembled contract terms. */
function ContractTerms({ markdown }: { markdown: string }) {
  const out: ReactNode[] = []
  let list: string[] = []
  let key = 0
  const flushList = () => {
    if (list.length) {
      out.push(
        <ul key={key++} className="list-disc pl-5 space-y-1 text-sm leading-relaxed text-foreground/90">
          {list.map((li, i) => (
            <li key={i}>{inlineMd(li)}</li>
          ))}
        </ul>
      )
      list = []
    }
  }
  for (const raw of markdown.split('\n')) {
    const line = raw.trim()
    if (!line || line.startsWith('<!--')) {
      flushList()
      continue
    }
    if (line === '---') {
      flushList()
      out.push(<hr key={key++} className="my-6 border-border" />)
      continue
    }
    if (line.startsWith('### ')) {
      flushList()
      out.push(<h3 key={key++} className="text-sm font-semibold mt-4">{inlineMd(line.slice(4))}</h3>)
      continue
    }
    if (line.startsWith('## ')) {
      flushList()
      out.push(<h2 key={key++} className="text-base font-semibold mt-5">{inlineMd(line.slice(3))}</h2>)
      continue
    }
    if (line.startsWith('# ')) {
      flushList()
      out.push(<h1 key={key++} className="text-lg font-bold mt-2">{inlineMd(line.slice(2))}</h1>)
      continue
    }
    if (line.startsWith('- ')) {
      list.push(line.slice(2))
      continue
    }
    flushList()
    out.push(
      <p key={key++} className="text-sm leading-relaxed text-foreground/90">
        {inlineMd(line)}
      </p>
    )
  }
  flushList()
  return <div className="space-y-2.5">{out}</div>
}

/** Public page shell — hoisted to module scope so it never remounts on re-render. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Wordmark size="sm" />
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:duration-300">
        {children}
      </main>
    </div>
  )
}

export default function DealContractPage() {
  const { token } = useParams<{ token: string }>()
  const brand = useBrand()
  const brandBaseName = splitBrandName(brand.appName)[0]
  const [contract, setContract] = useState<Contract | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [signedName, setSignedName] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [signError, setSignError] = useState<string | null>(null)
  const [justSigned, setJustSigned] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await api.get(`/api/contract/${token}`)
        if (!cancelled) setContract(res.data.data)
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err?.response?.status === 404
              ? `This agreement could not be found. Check the link, or contact ${brandBaseName}.`
              : 'Something went wrong loading this agreement. Please try again.'
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [token])

  const sign = async () => {
    setSubmitting(true)
    setSignError(null)
    try {
      const res = await api.post(`/api/contract/${token}/sign`, {
        signedName: signedName.trim(),
        agreed,
      })
      const checkoutUrl: string | null = res.data?.data?.checkoutUrl ?? null
      if (checkoutUrl) {
        window.location.href = checkoutUrl
        return
      }
      setJustSigned(true)
    } catch (err: any) {
      setSignError(err?.response?.data?.message ?? 'Could not sign the agreement. Please try again.')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading agreement…
        </div>
      </Shell>
    )
  }

  if (error || !contract) {
    return (
      <Shell>
        <div className="rounded-xl border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">{error ?? 'Agreement unavailable.'}</p>
        </div>
      </Shell>
    )
  }

  const alreadySigned = ['signed', 'paid', 'provisioned'].includes(contract.status)

  if (justSigned || alreadySigned) {
    return (
      <Shell>
        <div className="rounded-xl border bg-card p-8 text-center space-y-3">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:fade-in-0 motion-safe:duration-300" />
          <h1 className="text-xl font-semibold">Agreement signed</h1>
          <p className="text-sm text-muted-foreground">
            Thank you{contract.signedName ? `, ${contract.signedName}` : ''}. Your agreement with {brand.appName}
            is signed. Our team will be in touch with the next steps.
          </p>
        </div>
      </Shell>
    )
  }

  const dueNow = contract.monthlyTotalCents + contract.oneTimeTotalCents
  const canSign = agreed && signedName.trim().length >= 2 && !submitting

  return (
    <Shell>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Services Agreement</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Prepared for {contract.business || 'your firm'}
            {contract.contactName ? ` · ${contract.contactName}` : ''}
          </p>
          {contract.address ? (
            <p className="text-xs text-muted-foreground mt-0.5">{contract.address}</p>
          ) : null}
        </div>

        {/* Order summary */}
        <section className="rounded-xl border bg-card overflow-hidden">
          <div className="px-5 py-3 border-b bg-muted/30">
            <h2 className="text-sm font-semibold">Order summary</h2>
          </div>
          <div className="divide-y">
            {contract.items.map((it, i) => {
              const recurring = it.billing === 'recurring' || it.billing === 'pass_through'
              const lineCents = it.unitPriceCents * it.quantity
              return (
                <div key={i} className="px-5 py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium">
                        {it.name}
                        {it.quantity > 1 ? ` ×${it.quantity}` : ''}
                      </p>
                      {it.deliverables.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              aria-label={`What's included in ${it.name}`}
                              className="shrink-0 text-muted-foreground transition-[color,transform] duration-150 ease-out hover:text-brand-royal active:scale-90 motion-reduce:transition-none"
                            >
                              <HelpCircle className="h-3.5 w-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            sideOffset={6}
                            className="w-80 max-h-[min(26rem,72vh)] overflow-y-auto"
                          >
                            <p className="text-xs font-semibold">What&apos;s included</p>
                            {it.description ? (
                              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                                {it.description}
                              </p>
                            ) : null}
                            <ul className="mt-2.5 space-y-1.5">
                              {it.deliverables.map((d, di) => (
                                <li key={di} className="flex gap-2 text-xs leading-relaxed">
                                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                                  <span>{d}</span>
                                </li>
                              ))}
                            </ul>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{TERM_LABEL[it.term] ?? it.term}</p>
                    {it.managementFeePct ? (
                      <p className="text-xs text-muted-foreground">
                        + {it.managementFeePct}% management fee
                      </p>
                    ) : null}
                  </div>
                  <p className="text-sm font-mono whitespace-nowrap">
                    {fmt(lineCents)}
                    {recurring ? '/mo' : ''}
                  </p>
                </div>
              )
            })}
          </div>
          <div className="px-5 py-3 border-t bg-muted/30 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Recurring</span>
              <span className="font-mono">{fmt(contract.monthlyTotalCents)}/mo</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">One-time</span>
              <span className="font-mono">{fmt(contract.oneTimeTotalCents)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold pt-1 border-t">
              <span>Due today</span>
              <span className="font-mono">{fmt(dueNow)}</span>
            </div>
          </div>
        </section>

        {/* Terms & conditions — collapsed by default, kept out of the way */}
        <Collapsible className="rounded-xl border bg-card">
          <CollapsibleTrigger className="group flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-muted/30">
            <div>
              <h2 className="text-sm font-semibold">Terms &amp; conditions</h2>
              <p className="text-xs text-muted-foreground">The full Services Agreement — tap to read.</p>
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out group-data-[state=open]:rotate-180 motion-reduce:transition-none" />
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden data-[state=open]:motion-safe:animate-collapsible-down data-[state=closed]:motion-safe:animate-collapsible-up">
            <div className="border-t px-5 py-5">
              <ContractTerms markdown={contract.termsMarkdown} />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Sign */}
        <section className="rounded-xl border bg-card p-5 space-y-4">
          <h2 className="text-sm font-semibold">Sign agreement</h2>
          <label className="flex items-start gap-2.5 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="h-4 w-4 mt-0.5 rounded border-input accent-brand-royal"
            />
            <span>
              I have read and agree to this Services Agreement, including the terms and conditions, and I am
              authorized to enter into it on behalf of {contract.business || 'my firm'}.
            </span>
          </label>
          <div>
            <label className="text-[11px] text-muted-foreground uppercase tracking-wider block mb-1">
              Full name (typed signature)
            </label>
            <Input
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              placeholder="Your full name"
              className="max-w-sm"
            />
          </div>
          {signError && <p className="text-sm text-destructive">{signError}</p>}
          <Button onClick={sign} disabled={!canSign}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Sign &amp; continue to payment
          </Button>
          <p className="text-xs text-muted-foreground">
            Signing records your name, the date, and your IP address as your electronic signature.
            You&apos;ll then be taken to secure payment.
          </p>
        </section>
      </div>
    </Shell>
  )
}
